import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Parrot revision history component for viewing and comparing revisions
 */
const ParrotRevisionHistory = ({
  parrot,
  onClose,
  onRevert
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [revisions, setRevisions] = useState([]);
  const [selectedRevisions, setSelectedRevisions] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [reverting, setReverting] = useState(false);

  /**
   * Fetch revision history
   */
  const fetchRevisions = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/parrots/${parrot.id}/revisions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setRevisions(data.revisions || []);

    } catch (error) {
      console.error('Error fetching revisions:', error);
      showToast(error.message || 'Failed to load revision history', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle revision selection for comparison
   */
  const handleRevisionSelect = (revision) => {
    setSelectedRevisions(prev => {
      if (prev.find(r => r.id === revision.id)) {
        return prev.filter(r => r.id !== revision.id);
      } else if (prev.length < 2) {
        return [...prev, revision];
      } else {
        return [prev[1], revision];
      }
    });
  };

  /**
   * Handle revision revert
   */
  const handleRevert = async (revision) => {
    if (!confirm(`Are you sure you want to revert to revision from ${formatDate(revision.created_at)}?`)) {
      return;
    }

    try {
      setReverting(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/parrots/${parrot.id}/revert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ revision_id: revision.id })
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const updatedParrot = await response.json();
      onRevert(updatedParrot);
      showToast('Parrot reverted successfully', 'success');
      onClose();

    } catch (error) {
      console.error('Error reverting parrot:', error);
      showToast(error.message || 'Failed to revert parrot', 'error');
    } finally {
      setReverting(false);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Get change type badge styling
   */
  const getChangeTypeBadge = (changeType) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
    
    switch (changeType?.toLowerCase()) {
      case 'created':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-green-900/30 text-green-400 border border-green-500/30' 
            : 'bg-green-100 text-green-800 border border-green-200'
        }`;
      case 'updated':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-blue-900/30 text-blue-400 border border-blue-500/30' 
            : 'bg-blue-100 text-blue-800 border border-blue-200'
        }`;
      case 'voice_updated':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-purple-900/30 text-purple-400 border border-purple-500/30' 
            : 'bg-purple-100 text-purple-800 border border-purple-200'
        }`;
      case 'prompt_updated':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30' 
            : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
        }`;
      default:
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-gray-900/30 text-gray-400 border border-gray-500/30' 
            : 'bg-gray-100 text-gray-800 border border-gray-200'
        }`;
    }
  };

  /**
   * Render field comparison
   */
  const renderFieldComparison = (field, oldValue, newValue) => {
    if (oldValue === newValue) return null;

    return (
      <div key={field} className={`border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} rounded-lg p-4`}>
        <h5 className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2 capitalize`}>
          {field.replace(/_/g, ' ')}
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Old Value</div>
            <div className={`${
              theme === 'dark' 
                ? 'bg-red-900/20 border-red-500/30 text-gray-300' 
                : 'bg-red-50 border-red-200 text-red-800'
            } border rounded p-2 text-sm`}>
              {typeof oldValue === 'object' ? JSON.stringify(oldValue, null, 2) : (oldValue || 'Empty')}
            </div>
          </div>
          <div>
            <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>New Value</div>
            <div className={`${
              theme === 'dark' 
                ? 'bg-green-900/20 border-green-500/30 text-gray-300' 
                : 'bg-green-50 border-green-200 text-green-800'
            } border rounded p-2 text-sm`}>
              {typeof newValue === 'object' ? JSON.stringify(newValue, null, 2) : (newValue || 'Empty')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render revision comparison
   */
  const renderComparison = () => {
    if (selectedRevisions.length !== 2) return null;

    const [older, newer] = selectedRevisions.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const fields = ['name', 'prompt', 'voice_id', 'voice_settings', 'status'];

    return (
      <div className={`mt-6 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 shadow-sm`}>
        <div className="flex justify-between items-center mb-4">
          <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Revision Comparison</h4>
          <button
            onClick={() => {
              setShowComparison(false);
              setSelectedRevisions([]);
            }}
            className={`${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            ✕
          </button>
        </div>
        
        <div className={`mb-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Comparing revision from {formatDate(older.created_at)} with revision from {formatDate(newer.created_at)}
        </div>

        <div className="space-y-4">
          {fields.map(field => 
            renderFieldComparison(field, older.data?.[field], newer.data?.[field])
          ).filter(Boolean)}
        </div>
      </div>
    );
  };

  // Load revisions when component mounts
  useEffect(() => {
    if (parrot?.id) {
      fetchRevisions();
    }
  }, [parrot?.id]);

  return (
    <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border shadow-sm`} data-theme={theme}>
      {/* Header */}
      <div className={`px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
        <div>
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Revision History</h3>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {parrot.name || 'Unnamed Parrot'} - View and compare configuration changes
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {selectedRevisions.length === 2 && (
            <button
              onClick={() => setShowComparison(!showComparison)}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors dark:text-white"
            >
              {showComparison ? 'Hide' : 'Show'} Comparison
            </button>
          )}
          <button
            onClick={onClose}
            className={`${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400 mx-auto"></div>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-4`}>Loading revision history...</p>
          </div>
        ) : revisions.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">📝</div>
            <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>No Revisions Found</h4>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              This parrot has no revision history available.
            </p>
          </div>
        ) : (
          <>
            {/* Instructions */}
            <div className={`mb-6 ${
              theme === 'dark' 
                ? 'bg-blue-900/20 border-blue-500/30 text-blue-300' 
                : 'bg-blue-50 border-blue-200 text-blue-700'
            } border rounded-lg p-4`}>
              <p className="text-sm">
                Select up to 2 revisions to compare changes. Click "Revert" to restore a previous configuration.
              </p>
            </div>

            {/* Revisions list */}
            <div className="space-y-4">
              {revisions.map((revision, index) => (
                <div
                  key={revision.id}
                  className={`border rounded-lg p-4 transition-colors cursor-pointer ${
                    selectedRevisions.find(r => r.id === revision.id)
                      ? theme === 'dark'
                        ? 'border-teal-500 bg-teal-900/20'
                        : 'border-teal-500 bg-teal-50'
                      : theme === 'dark'
                        ? 'border-gray-600 hover:border-gray-500'
                        : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => handleRevisionSelect(revision)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={getChangeTypeBadge(revision.change_type)}>
                          {revision.change_type || 'Updated'}
                        </span>
                        <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {formatDate(revision.created_at)}
                        </span>
                        {index === 0 && (
                          <span className={`px-2 py-1 ${
                            theme === 'dark' 
                              ? 'bg-teal-900/30 text-teal-400 border-teal-500/30' 
                              : 'bg-teal-100 text-teal-800 border-teal-200'
                          } border rounded-full text-xs font-medium`}>
                            Current
                          </span>
                        )}
                      </div>
                      
                      <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        Changed by: {revision.changed_by_name || revision.changed_by_email || 'System'}
                      </div>

                      {revision.change_summary && (
                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                          {revision.change_summary}
                        </div>
                      )}

                      {revision.data && (
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                          Configuration snapshot available
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      {selectedRevisions.find(r => r.id === revision.id) && (
                        <span className={`${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'} text-sm`}>Selected</span>
                      )}
                      {index > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRevert(revision);
                          }}
                          disabled={reverting}
                          className="px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors disabled:opacity-50 dark:text-white"
                        >
                          {reverting ? 'Reverting...' : 'Revert'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Comparison panel */}
            {showComparison && renderComparison()}
          </>
        )}
      </div>
    </div>
  );
};

export default ParrotRevisionHistory;
